import React from "react";
import { useForm, type SubmitHandler } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useBudgetFunctionalityDisabledError } from "@features/Budget/hooks/useBudgetFunctionalityDisabledError";
import { Button } from "@components/Button";
import { PlusIcon } from "@components/icons";
import { FormInput } from "@components/inputs/form/FormInput/FormInput";
import { Modal } from "@components/Modal/Modal";
import { useApiErrorUtils } from "@hooks/useApiErrorUtils";
import { useModal } from "@hooks/useModal";
import { type IUseModalProps } from "@store/modal/modal";
import { useIncrementSavingsPlanAchievedAmountMutation } from "@services/budget/mutations";
import { parseDecimalNumberInputValue } from "@utils/units";

interface IIncrementSavingsPlanModalProps extends IUseModalProps {
  savingsPlanId: number;
}

interface IFormValues {
  incrementAmount: number | string;
}

export const IncrementSavingsPlanModal: React.FC<
  IIncrementSavingsPlanModalProps
> = (props) => {
  const { savingsPlanId, ...rest } = props;
  const { t } = useTranslation();
  const modal = useModal(rest);

  const { control, setValue, getValues, handleSubmit, setError } =
    useForm<IFormValues>({
      defaultValues: {
        incrementAmount: 0,
      },
    });

  const { setApiErrors } = useApiErrorUtils(setError);
  const { handleBudgetError } = useBudgetFunctionalityDisabledError();

  const { mutateAsync, isPending } =
    useIncrementSavingsPlanAchievedAmountMutation();

  const onSubmit: SubmitHandler<IFormValues> = async (data) => {
    const result = await mutateAsync({
      savingsPlanId,
      incrementAmount: parseDecimalNumberInputValue(
        data.incrementAmount.toString(),
      ),
    }).catch(handleBudgetError(setApiErrors));

    if (result) {
      toast.success(t("budget.savings.incrementSavingsPlanModal.success"));
      modal.close();
    }
  };

  return (
    <Modal
      modal={modal}
      title={t("budget.savings.incrementSavingsPlanModal.title")}
      badgeProps={{ icon: PlusIcon, color: "green" }}
      actionButtons={
        <React.Fragment>
          <Button className="w-full" variant="secondary" onClick={modal.close}>
            {t("common.form.buttons.cancel")}
          </Button>
          <Button
            className="w-full"
            onClick={handleSubmit(onSubmit)}
            isLoading={isPending}
          >
            {t("common.form.buttons.submit")}
          </Button>
        </React.Fragment>
      }
    >
      <div className="flex flex-col gap-4">
        <FormInput
          control={control}
          name="incrementAmount"
          label={t("budget.savings.incrementSavingsPlanModal.amount")}
          onBlur={() =>
            setValue(
              "incrementAmount",
              parseDecimalNumberInputValue(
                getValues("incrementAmount").toString(),
              ),
            )
          }
        />
      </div>
    </Modal>
  );
};
